// Rebuilds the store's per-game `userOrders` entries from `user/getUnmatched`.
//
// The rendered price feed is anonymous, so the client keeps its own record of
// which book rows are the user's (exchange store `userOrders`, read by
// applyAggregateOwnership). The page-scoped orderbook endpoints attach that
// record as `orderSummary` in a per-game shape, but only for one page's
// games; getUnmatched is the cheap, already-indexed source for ALL of the
// user's open orders (one Session.find on the User Orderbook index). It
// returns a flat, maker-perspective row per order, so this module re-derives
// the per-game summary shape client-side -- the same fields the server's
// buildPortion emits -- rather than asking the API to render it.
//
// getUnmatched carries no matched positions, so entries are MERGED: only the
// `unmatched` list of each game is rebuilt; `matched` / `orderSummary` (the
// P&L the exchange and game views read) stay as the last page load or
// position-update left them. Orders the server already rendered keep their
// richer object (description etc.) and only pick up the fresh remaining
// stake; orders the client has never seen are synthesized below.

import { convertAmericanToDecimal } from '@/core/utils';

const YES_NO_FLIP = { yes: 'no', no: 'yes' };
const OVER_UNDER_FLIP = { over: 'under', under: 'over' };

function capitalize(value) {
  const text = value == null ? '' : String(value);
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

// Mirrors the API's displaySign: "+150" / "-110" as strings, 0 stays 0.
function signLine(value) {
  const n = Number(value);
  if (!n) return n;
  return n > 0 ? `+${n}` : `${n}`;
}

// A row's spread/side/OU/odds are the MAKER's view (getUnmatched flips them
// for the order's owner). The summary shape keeps spread/side as stored on
// the session (book/taker view) and odds/OU maker-side, so only some flip.
function negateLine(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n === 0) return 0;
  return -n;
}

function idString(value) {
  if (value == null) return null;
  const text = typeof value === 'object' && value.id ? value.id : value;
  return String(text);
}

function participantsOf(game) {
  return Array.isArray(game?.participants) ? game.participants : [];
}

// Rendered games list [away, home]; resolve by homeAway rather than index so a
// differently ordered payload cannot swap sides.
function homeParticipantID(game) {
  const home = participantsOf(game).find((p) => p?.homeAway === 'home');
  return idString(home?.id ?? participantsOf(game)[1]?.id);
}

function participantByID(game, id) {
  const wanted = idString(id);
  if (!wanted) return null;
  return participantsOf(game).find((p) => idString(p?.id) === wanted) ?? null;
}

// Same rules as the API's getHomeAwayForSession. `makerParticipantID` is the
// side the user actually bet (getUnmatched's participantID).
function homeAwayFor(row) {
  const homeID = homeParticipantID(row.game);
  if (row.type === 'total') return { homeAway: null, takerHomeAway: null };

  if (row.type === 'moneyline1x2') {
    const market = row.market == null ? null : String(row.market);
    if (!market || !homeID || market.toLowerCase() === 'draw') {
      return { homeAway: null, takerHomeAway: null };
    }
    const side = market === homeID ? 'home' : 'away';
    return { homeAway: side, takerHomeAway: side };
  }

  const makerID = idString(row.participantID);
  if (!makerID || !homeID) return { homeAway: null, takerHomeAway: null };
  const homeAway = makerID === homeID ? 'home' : 'away';
  return { homeAway, takerHomeAway: homeAway === 'home' ? 'away' : 'home' };
}

// Same rules as the API's getLongType.
function longTypeFor(row, takerHomeAway, takerOU) {
  if (row.type === 'total') return `${takerOU ?? ''}`;
  if (row.type === 'moneyline1x2') {
    if (takerHomeAway === null) return 'draw1x2';
    return takerHomeAway === 'home' ? 'homeMoneylines1x2' : 'awayMoneylines1x2';
  }
  return `${takerHomeAway ?? ''}${capitalize(row.type)}s`;
}

// Same text as the API's getOrderDescription, from the maker's view the row
// already carries (team the user bet, the user's line/side).
function descriptionFor(row) {
  const game = row.game;
  const isFutures = Boolean(
    game?.isFutures ||
      String(game?.league ?? '')
        .toLowerCase()
        .includes('future'),
  );
  if (row.type === 'moneyline') {
    const team = participantByID(game, row.participantID);
    if (!team) return '';
    return isFutures ? capitalize(team.futuresSide) : (team.shortName ?? '');
  }
  if (row.type === 'moneyline1x2') {
    const market = row.market == null ? '' : String(row.market);
    const side = capitalize(row.side ?? '');
    if (market.toLowerCase() === 'draw') return `Draw ${side}`.trim();
    const team = participantByID(game, market);
    return `${team?.shortName ?? 'Unknown'} ${side}`.trim();
  }
  if (row.type === 'spread') {
    const team = participantByID(game, row.participantID);
    return `${team?.shortName ?? ''} ${signLine(row.spread)}`.trim();
  }
  if (row.type === 'total') {
    return `${capitalize(row.OU)} ${row.total}`.trim();
  }
  return '';
}

// One getUnmatched row -> one order in the summary's `unmatched` shape.
export function unmatchedRowToOrder(row) {
  const risk = Number(row.remaining) || 0;
  // `win` here is what the taker would win against the remaining stake --
  // the level-volume units applyAggregateOwnership subtracts as the user's
  // own share. Row odds are already maker-side, so the taker payout factor
  // is dec(rowOdds) - 1 exactly as buildPortion computes it from -session.odds.
  const takerPayoutFactor = convertAmericanToDecimal(Number(row.odds)) - 1;
  const { homeAway, takerHomeAway } = homeAwayFor(row);
  const makerOU = row.type === 'total' ? (row.OU ?? null) : null;
  const takerOU = makerOU ? (OVER_UNDER_FLIP[makerOU] ?? null) : null;
  const makerSide = row.type === 'moneyline1x2' ? (row.side ?? null) : null;
  const sessionSide = makerSide ? (YES_NO_FLIP[makerSide] ?? null) : null;

  return {
    risk,
    win: risk * takerPayoutFactor,
    homeAway,
    takerHomeAway,
    odds: signLine(row.odds),
    longType: longTypeFor(row, takerHomeAway, takerOU),
    description: descriptionFor(row),
    orderID: String(row.id),
    type: row.type,
    OU: makerOU,
    total: row.total ?? null,
    spread: negateLine(row.spread),
    // Session (book) view, like buildPortion: the participant the row is
    // stored against, and the taker-facing yes/no. Deliberately NO
    // `participantID` key -- the level matcher reads participantID before
    // participant and would otherwise compare the maker's team.
    participant: idString(row.otherParticipantID),
    market: row.type === 'moneyline1x2' ? (row.market ?? null) : null,
    side: sessionSide,
    bettorYesNo: makerSide,
  };
}

// Own-order record -> a PROVISIONAL book-view session for EditOrderModal, so
// the modal can open at once instead of waiting for the open-offers fetch.
// Same conversion the store's flipRealSessionToBook applies to a
// getOpenOffers row. The record is maker-side for odds/OU and book-side for
// spread/side/market/participant (see unmatchedRowToOrder above and the
// server's buildPortion), so only odds and OU flip. `risk` is already the
// remaining stake, so bet is the remainder and takenRatio stays 0, exactly
// how the store stamps an aggregate row.
//
// The record carries no expiry, so the result is never safe to SUBMIT: the
// store marks it expiryPending and replaces it with the authoritative order
// once fetched. Returns null when the record cannot describe a real order.
export function ownOrderToBookSession(order, { gameID, userID }) {
  if (!order || order.orderID == null) return null;
  const makerOdds = Number(order.odds);
  const bet = Number(order.risk);
  if (!Number.isFinite(makerOdds) || makerOdds === 0) return null;
  if (!Number.isFinite(bet) || bet <= 0) return null;

  const type = order.type;
  const isTotal = type === 'total';
  const is1x2 = type === 'moneyline1x2';
  const market = is1x2 && order.market != null ? String(order.market) : null;
  // A 1x2 order is only identified by market + side; without both, the
  // record cannot say which outcome is being edited.
  if (is1x2 && (!market || order.side == null)) return null;

  const bookOU = isTotal ? (OVER_UNDER_FLIP[order.OU] ?? null) : null;
  if (isTotal && !bookOU) return null;

  let participantID = null;
  if (is1x2) {
    participantID = market !== 'draw' ? market : null;
  } else if (!isTotal) {
    participantID = idString(order.participant);
    if (!participantID) return null;
  }

  return {
    id: String(order.orderID),
    bet,
    createdBy: userID,
    expiry: null,
    gameStartExpiry: true,
    sumUntaken: bet * (convertAmericanToDecimal(-makerOdds) - 1),
    type,
    odds: -makerOdds,
    spread: type === 'spread' ? Number(order.spread) || 0 : null,
    total: isTotal ? (order.total ?? null) : null,
    OU: bookOU,
    participantID,
    takenRatio: 0,
    market,
    side: is1x2 ? order.side : null,
    gameID,
  };
}

function emptyPositionSummary() {
  return {
    ml: { homeRisk: 0, awayRisk: 0, homeWin: 0, awayWin: 0 },
    spread: { homeRisk: 0, awayRisk: 0, homeWin: 0, awayWin: 0 },
    total: { overRisk: 0, underRisk: 0, overWin: 0, underWin: 0 },
    moneyline1x2: { awayYes: 0, homeYes: 0, drawYes: 0 },
  };
}

// A 1x2 game has six outcomes (home/away/draw x yes/no) that routinely share
// a price; market + side ARE a 1x2 order's identity. The level matcher treats
// both as optional (absent = pass), so a 1x2 record missing either would be
// stamped onto every outcome at that price -- and the stamp sets the row's id
// to the order id, letting cancel/edit from the wrong row target the real
// order. Fail closed: such a row (e.g. from an API build that does not emit
// the two fields yet) never enters the store, so it matches nothing. Its
// highlight returns on the next page load / position update, which carry
// both fields.
function hasUsableIdentity(row) {
  if (row?.type !== 'moneyline1x2') return true;
  return row.market != null && row.market !== '' && row.side != null;
}

// Rows whose game failed to resolve come back as { _id, error } with no `id`;
// there is no book row to stamp for those, so they are skipped.
export function groupUnmatchedRowsByGame(rows) {
  const groups = new Map();
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const gameID = idString(row?.game?.id);
    if (!gameID || row.game.error) return;
    if (!groups.has(gameID)) {
      groups.set(gameID, {
        renderedGame: row.game,
        orders: [],
        // Open orders the server confirmed but this row cannot identify
        // (see hasUsableIdentity). The merge keeps an existing, identifiable
        // record for these instead of synthesizing or dropping one.
        unidentifiedOrderIDs: new Set(),
      });
    }
    const group = groups.get(gameID);
    if (!hasUsableIdentity(row)) {
      group.unidentifiedOrderIDs.add(String(row.id));
      return;
    }
    group.orders.push(unmatchedRowToOrder(row));
  });
  return groups;
}

// Fresh list for one game: keep the server-rendered object for any order the
// client already has (it carries fields this module only approximates), but
// take the fresh remaining stake; synthesize the rest.
function reconcileUnmatched(existingOrders, group) {
  const byID = new Map();
  (Array.isArray(existingOrders) ? existingOrders : []).forEach((order) => {
    if (order?.orderID != null) byID.set(String(order.orderID), order);
  });
  const reconciled = group.orders.map((fresh) => {
    const existing = byID.get(fresh.orderID);
    return existing ? { ...existing, risk: fresh.risk, win: fresh.win } : fresh;
  });
  // Server says these are still open but could not identify them; an
  // existing record (from a page load / position update, which carry the
  // identity) is the only usable version, so keep it exactly as it is.
  group.unidentifiedOrderIDs.forEach((orderID) => {
    const existing = byID.get(orderID);
    if (existing) reconciled.push(existing);
  });
  return reconciled;
}

// Pure merge of the complete open-order set into the current entries.
// Returns the new entries array and the ids of every game touched (union of
// before and after) so the caller can re-stamp exactly those.
//
// `skipGameIDs`: games the caller knows have NEWER data than `rows` (a live
// position-update landed after the HTTP request was sent). The HTTP body is
// a snapshot from when the server built it, so for those games it would
// overwrite fresh state with stale state -- e.g. re-adding an order the live
// feed just reported matched, leaving a green row for an order that is gone
// until the next event on that game. Skipped games are left exactly as they
// are, in both directions (not rebuilt, not cleared, not added).
export function mergeUnmatchedIntoUserOrders(
  existingEntries,
  rows,
  { skipGameIDs } = {},
) {
  const groups = groupUnmatchedRowsByGame(rows);
  const current = Array.isArray(existingEntries) ? existingEntries : [];
  const skip = new Set(
    skipGameIDs ? Array.from(skipGameIDs, (id) => idString(id)) : [],
  );
  const affected = new Set();
  const seen = new Set();

  const entries = current.map((entry) => {
    const gameID = idString(entry?.renderedGame?.id);
    if (!gameID) return entry;
    seen.add(gameID);
    if (skip.has(gameID)) return entry;
    const group = groups.get(gameID);
    const hadOrders =
      Array.isArray(entry.unmatched) && entry.unmatched.length > 0;
    if (!group) {
      if (!hadOrders) return entry;
      affected.add(gameID);
      return { ...entry, unmatched: [] };
    }
    affected.add(gameID);
    return {
      ...entry,
      unmatched: reconcileUnmatched(entry.unmatched, group),
    };
  });

  groups.forEach((group, gameID) => {
    if (seen.has(gameID) || skip.has(gameID)) return;
    // Nothing identifiable to add for a game the store has never seen.
    if (group.orders.length === 0) return;
    affected.add(gameID);
    entries.push({
      renderedGame: group.renderedGame,
      matched: [],
      unmatched: group.orders,
      orderSummary: emptyPositionSummary(),
    });
  });

  return { entries, affected };
}
